import { useState, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '~/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/components/ui/dialog';
import { Label } from '~/components/ui/label';
import { Textarea } from '~/components/ui/textarea';

import { useStopExperiment } from '../../hooks/use-experiment-stop';
import type { ExperimentStopFinalStatus } from '../../services/experiment-stop.service';

/**
 * Resolved confirm payload for a single terminal action — built upstream in
 * `useExperimentActions` from the action id, so the dialog stays
 * presentation-only. The `defaultReason` becomes the textarea's initial
 * value (operator can override before submitting).
 */
export interface TerminalActionConfig {
  /** Title shown at the top of the dialog (e.g. "End experiment"). */
  title: string;
  /** Helper line under the title — explains the outcome and that it's irreversible. */
  description: string;
  /** Body of the request sent on confirm. */
  finalStatus: ExperimentStopFinalStatus;
  /** Pre-filled audit string the operator can amend in the textarea. */
  defaultReason: string;
  /** Label for the primary confirm button (e.g. "End experiment", "Confirm scaling"). */
  confirmLabel: string;
}

interface Props {
  /** Null = closed. Setting a config opens the dialog for that action. */
  config: TerminalActionConfig | null;
  experimentId: string;
  onClose: () => void;
}

/**
 * Confirmation modal for the chatbot's terminal-action buttons
 * (End / End Without Scaling / Confirm Scaling / Scale Winner).
 *
 * Pre-configured per action via {@link TerminalActionConfig} — the dialog
 * itself doesn't know which CTA opened it, only what to send. On confirm it
 * fires `POST /experiments/{id}/stop` with `{ finalStatus, reason }`;
 * success closes the dialog and triggers cache invalidation in
 * {@link useStopExperiment}. 409 / generic errors are toasted by the hook.
 */
export function ConfirmTerminalActionDialog({ config, experimentId, onClose }: Props) {
  const { t } = useTranslation('experiments');
  const stop = useStopExperiment();
  const [reason, setReason] = useState(config?.defaultReason ?? '');
  // Render-phase tracking: when the caller opens the dialog with a fresh
  // config, reset the textarea to that config's default reason. Using
  // setState-during-render here (canonical React docs pattern) avoids the
  // project's `set-state-in-effect` rule rejecting a useEffect.
  const [trackedConfig, setTrackedConfig] = useState(config);
  if (config !== trackedConfig) {
    setTrackedConfig(config);
    setReason(config?.defaultReason ?? '');
  }

  const handleConfirm = () => {
    if (!config) return;
    const trimmed = reason.trim();
    stop.mutate(
      {
        expId: experimentId,
        payload: {
          finalStatus: config.finalStatus,
          // Fall back to the canned reason if the operator cleared the
          // textarea — the API requires a non-empty string.
          reason: trimmed.length > 0 ? trimmed : config.defaultReason,
        },
      },
      {
        onSuccess: () => {
          onClose();
        },
      },
    );
  };

  return (
    <Dialog
      open={config !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{config?.title ?? ''}</DialogTitle>
          <DialogDescription>{config?.description ?? ''}</DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <Label htmlFor="terminal-action-reason" className="text-sm font-medium">
            {t('detail.stop.reasonLabel')}
          </Label>
          <Textarea
            id="terminal-action-reason"
            rows={3}
            value={reason}
            onChange={(e: ChangeEvent<HTMLTextAreaElement>) => {
              setReason(e.target.value);
            }}
            placeholder={t('detail.stop.reasonPlaceholder')}
          />
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            disabled={stop.isPending}
            isLoading={stop.isPending}
            className="cursor-pointer"
          >
            {t('detail.stop.cancelButton')}
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={stop.isPending || config === null}
            isLoading={stop.isPending}
            className="cursor-pointer"
          >
            {config?.confirmLabel ?? ''}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
